import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RepairOrder } from 'src/models';
import { AxiosError } from 'axios';
import * as acBase from 'src/store/states/GenericActionCreator';
import { IStoreState } from 'src/store';
import { ViewMode } from 'src/enums';
import { xml2js, ElementCompact } from 'xml-js';
export interface IRepairOrdersState {
    RepairOrders: RepairOrder[];
    currentRepairOrder?: string;
    loadingROs: boolean;
    reyPAYUrl: string;
    errMsg?: string;
}

/** Initial State */
export const initialState: IRepairOrdersState = {
    RepairOrders: [],
    loadingROs: false,
    reyPAYUrl: '',
    errMsg: '',
};

export const RepairOrdersSlice = createSlice({
    name: 'RepairOrders',
    initialState: initialState,
    reducers: {
        toggleLangLits: (state, action: PayloadAction<RepairOrder[]>) => {
            state.RepairOrders = action.payload;
        },
        getROs: (state, action: PayloadAction<IRepairOrdersState>) => {
            state.RepairOrders = action.payload.RepairOrders;
            state.currentRepairOrder = action.payload.currentRepairOrder;
            state.errMsg = action.payload.errMsg;
        },
        selectRO: (state, action: PayloadAction<string>) => {
            state.currentRepairOrder = action.payload;
        },
        setLoadingROs: (state, action: PayloadAction<boolean>) => {
            state.loadingROs = action.payload;
        },
        clearRO: (state) => {
            state.RepairOrders = initialState.RepairOrders;
            state.loadingROs = false;
            state.currentRepairOrder = undefined;
            state.reyPAYUrl = '';
        },
        setReyPAYUrl: (state, action: PayloadAction<string>) => {
            state.reyPAYUrl = action.payload ? action.payload : 'NONE';
        },
        logDocsHistory: (state, action: PayloadAction<string>) => {
            state.errMsg = action.payload;
        },
    },
});

export const {
    getROs,
    selectRO,
    setLoadingROs,
    clearRO,
    setReyPAYUrl,
    logDocsHistory,
    toggleLangLits,
} = RepairOrdersSlice.actions;

export default RepairOrdersSlice.reducer;

export function FetchROs(customerNum: string, roNum?: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const specProfile = getState().Framework.specProfile;
        const repairOrders = getState().RepairOrders;
        const serviceDocsFlag = getState().Framework.serviceDocsFlag;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            customerNum: customerNum,
            profileNum: specProfile,
            serviceDocsFlag: serviceDocsFlag,
            logId: logId,
            callingProgram: callingProgram,
        };
        dispatch(setLoadingROs(true));
        let response = await acBase.eraWebServiceCall<object>('SVPController', 'GetROs', reqData, store, branch);
        if (response && (response as AxiosError).isAxiosError) {
            response = { errMsg: (response as AxiosError).message };
        }
        if ((response as { errMsg: string }).errMsg && response.errMsg !== '8000') {
            response = repairOrders;
        }
        if (!(response as AxiosError).isAxiosError && !(response as { errMsg: string }).errMsg) {
            response = {
                RepairOrders: response.RepairOrders,
                loadingROs: repairOrders.loadingROs,
            };
        }
        dispatch(getROs(response));
        if (roNum) {
            dispatch(selectRO(roNum));
        } else {
            dispatch(selectRO('-1'));
        }
        dispatch(setLoadingROs(false));
    };
}

export function SelectRO(roNum: string) {
    return (dispatch) => {
        dispatch(selectRO(roNum));
    };
}

export function SetLoadingROs(setLoading: boolean) {
    return (dispatch) => {
        dispatch(setLoadingROs(setLoading));
    };
}

export function ClearROState() {
    return (dispatch) => {
        dispatch(clearRO());
    };
}

export function GetReyPAYUrl() {
    return async (dispatch, getState: () => IStoreState) => {
        const userName = 'DMS Application User';
        const pwd = '$Integration';
        const sysId = getState().Framework.sysId;
        const storeNo = getState().Framework.store;
        const branchNo = getState().Framework.branch;
        const roNumber = getState().RepairOrders.currentRepairOrder;
        const sysNum = window.location.href.slice(11, 16);
        const sysType = 'ERA';
        const appCode = 'SVC';
        let url = 'https://leads.cm.reyrey.com/services/IntegrationService.asmx';
        const type = window.location.href.slice(8, 10).toUpperCase();
        if (sysNum === 'lhost') {
            url = '/services-leads/IntegrationService.asmx';
        }
        if (type === 'RD' || type === 'RI') {
            url = 'https://leads-qa01.reyqa.com/services/IntegrationService.asmx';
        }
        const sr = '<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ' +
            'xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">' +
            '<soap:Body>' +
                '<RetrieveROFullInvoiceLink xmlns="http://reyrey.com/">' +
                    '<Credentials>' +
                        '<Username>' + userName + '</Username>' +
                        '<Password>' + pwd + '</Password>' +
                        '<SysType>' + sysType + '</SysType>' +
                        '<SysID>' + sysId + '</SysID>' +
                        '<StoreNo>' + storeNo + '</StoreNo>' +
                        '<BranchNo>' + branchNo + '</BranchNo>' +
                        '<DMSSysNum>' + sysNum + '</DMSSysNum>' +
                        '<AppCode>' + appCode + '</AppCode>' +
                    '</Credentials>' +
                    '<RepairOrderRequest>' +
                        '<RONumber>' + roNumber + '</RONumber>' +
                    '</RepairOrderRequest>' +
                '</RetrieveROFullInvoiceLink>' +
            '</soap:Body>' +
        '</soap:Envelope>';
        const header = {
            'content-type': 'text/xml; charset=utf-8',
            'SOAPAction': 'http://reyrey.com/RetrieveROFullInvoiceLink',
            'Access-Control-Allow-Origin': sysNum === 'lhost' ? '*' : window.location.origin,
        };
        const soapRequest = require('easy-soap-request');
        const { response } = await soapRequest({ url: url, headers: header, xml: sr });
        const { body, statusCode } = response;
        if (statusCode === 200) {
            const rtn = xml2js(body, { compact: true, textKey: 'text' }) as ElementCompact;
            const soapBody = rtn['soap:Envelope']['soap:Body'];
            const result = soapBody.RetrieveROFullInvoiceLinkResponse.RetrieveROFullInvoiceLinkResult.ROGeneratedURL.text;
            dispatch(setReyPAYUrl(result));
        } else {
            dispatch(setReyPAYUrl('NONE'));
        }
    };
}

export function LogDocsHistory(keyRO: string, action: string, docType: string, details: string, idmUrl: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            keyRO: keyRO,
            action: action,
            docType: docType,
            details: details,
            idmUrl: idmUrl,
            logId: logId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<object>('SVPController', 'LogDocsHistory', reqData, store, branch);
        if (response?.errMsg === '8000') {
            dispatch(logDocsHistory(response.errMsg));
        } else {
            dispatch(logDocsHistory(response));
        }
    };
}

export function ToggleROLangLits(language: string) {
    return (dispatch, getState: () => IStoreState) => {
        const repairOrderState = {...getState().RepairOrders};

        const returnROState: RepairOrder[] = repairOrderState.RepairOrders.map(ro => {
            const outputDmsStatuses: any = [];
            const outputRO: any = {...ro};
            if (language === 'fr-CA') {
                ro.dmsStatuses.forEach(dmsStatus => {
                    outputDmsStatuses.push({
                        status: dmsStatus.statusFR,
                        webTitle: dmsStatus.webTitleFR,
                        statusEN: dmsStatus.statusEN,
                        webTitleEN: dmsStatus.webTitleEN,
                        statusFR: dmsStatus.statusFR,
                        webTitleFR: dmsStatus.webTitleFR,
                    });
                });
            } else {
                ro.dmsStatuses.forEach(dmsStatus => {
                    outputDmsStatuses.push({
                        status: dmsStatus.statusEN,
                        webTitle: dmsStatus.webTitleEN,
                        statusEN: dmsStatus.statusEN,
                        webTitleEN: dmsStatus.webTitleEN,
                        statusFR: dmsStatus.statusFR,
                        webTitleFR: dmsStatus.webTitleFR,
                    });
                });
            }
            outputRO.dmsStatuses = outputDmsStatuses;

            return outputRO;
        });
        dispatch(toggleLangLits(returnROState));
    };
}